import { createFileRoute, Link } from "@tanstack/react-router";
import { Palette, Users, Eye, Sparkles, Figma, Accessibility, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/services/ui-ux-design/")({
  component: UxIndex,
  head: () => ({
    meta: [
      { title: "UI / UX Design Services — CoderLity" },
      { name: "description", content: "User-centric product design, design systems and interaction design that drives conversion." },
      { property: "og:title", content: "UI / UX Design Services — CoderLity" },
      { property: "og:description", content: "Product design that earns trust, removes friction and lifts conversion." },
      { property: "og:url", content: "/services/ui-ux-design" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/services/ui-ux-design" }],
  }),
});

const design = [
  { slug: "product-design", title: "Product Design", desc: "Explore product design" },
  { slug: "web-design", title: "Web Design", desc: "Explore web design" },
  { slug: "mobile-design", title: "Mobile Design", desc: "Explore mobile design" },
  { slug: "design-systems", title: "Design Systems", desc: "Explore design systems" },
];

const research = [
  { slug: "user-research", title: "User Research", desc: "Explore user research" },
  { slug: "usability-testing", title: "Usability Testing", desc: "Explore usability testing" },
  { slug: "wireframing", title: "Wireframing", desc: "Explore wireframing" },
];

const branding = [
  { slug: "brand-identity", title: "Brand Identity", desc: "Explore brand identity" },
  { slug: "logo-visual", title: "Logo & Visual", desc: "Explore logo & visual" },
  { slug: "motion-graphics", title: "Motion Graphics", desc: "Explore motion graphics" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/services/ui-ux-design/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function UxIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Design" items={design} />
        <Group title="Research" items={research} />
        <Group title="Branding" items={branding} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Services",
  title: "UI / UX Design",
  sub: "User-centric product design and experiences.",
  intro:
    "Great design is invisible. Users get what they came for, faster — and your conversion rate goes up. We blend research-driven UX with pixel-perfect UI to ship interfaces that delight users and move metrics. Every screen is prototyped, tested, and accompanied by a design system your engineers will love.",
  features: [
    { icon: Users, title: "User research", description: "Interviews, surveys and usability tests with real customers." },
    { icon: Eye, title: "Visual identity", description: "Brand-aligned color, typography and component design." },
    { icon: Sparkles, title: "Interaction design", description: "Micro-interactions and motion that make products feel alive." },
    { icon: Palette, title: "Design systems", description: "Reusable tokens and components documented in Figma." },
    { icon: Figma, title: "Developer handoff", description: "Pixel-perfect specs, prototypes and Storybook-ready specs." },
    { icon: Accessibility, title: "Accessibility first", description: "WCAG 2.2 AA compliant from concept to ship." },
  ],
  process: [
    { step: "01", title: "Discover", description: "User interviews, competitive audit and goals mapping." },
    { step: "02", title: "Define", description: "Information architecture, user flows and wireframes." },
    { step: "03", title: "Design", description: "High-fidelity UI, prototypes and usability testing." },
    { step: "04", title: "Deliver", description: "Design system, dev handoff and post-launch iteration." },
  ],
  deliverables: [
    "Research report and user personas",
    "Wireframes and interactive prototypes",
    "High-fidelity UI for every screen",
    "Complete design system in Figma",
    "Brand guidelines and asset library",
    "Developer handoff documentation",
  ],
  techStack: ["Figma", "FigJam", "Maze", "Lottie", "Framer", "Principle", "Storybook"],
  pricing: [
    { name: "UI refresh", price: "$4k", features: ["Up to 10 screens", "Style guide", "Component library", "3 weeks"] },
    { name: "Product design", price: "$15k", period: "project", highlighted: true, features: ["Full research", "Design system", "Prototype + testing", "8–10 weeks"] },
    { name: "Design partner", price: "$7k", period: "month", features: ["Dedicated designer", "Weekly design reviews", "Continuous iteration", "Design ops"] },
  ],
  faqs: [
    { q: "Do you do design only, or also build?", a: "We do both. You can hire us for design only, or as a full design + engineering partner where everything ships in lockstep." },
    { q: "Will my team be able to maintain the design?", a: "Yes. We deliver a documented design system in Figma so your designers and developers can extend it without us." },
    { q: "Do you run user testing?", a: "Yes — moderated and unmoderated tests with 5–8 users per round, with video clips and clear action items." },
    { q: "Can you redesign an existing product?", a: "Absolutely. We start with a heuristic audit and analytics review before proposing any visual changes, so we keep what works." },
  ],
};
